import React, { useState, useEffect } from "react";
import axios from "axios";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import AsyncSelect from "react-select/async";
import { Box, Button, Modal, Typography } from "@mui/material";
import TextField from "@mui/material/TextField";
import { api } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toast";

const validationSchema = Yup.object().shape({
  // partnerCompany: Yup.object().required("Partner Company is required"),
  // product: Yup.object().required("Product is required"),
  price: Yup.number()
    .required("Price is required")
    .min(0, "Price must be greater than or equal to 0")
    .test("decimal", "Price can have at most 2 decimal places", (value) => {
      if (value === undefined || value === null) return true;
      return Number.isInteger(value * 100);
    })
});

const PartnerPricingForm = ({ selectedPartnerPricing, callback, cancelModal, defaultCompany }) => {
  // console.log(selectedPartnerPricing);
  const [modalActive, setModalActive] = useState(true);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [partnerCompany, setPartnerCompany] = useState("");
  const [product, setProduct] = useState("");

  const getAllPartnerCompanies = async () => {
    try {
      const response = await api("v1/partner/company", "GET", null);
      if (response.data.success) {
        const allCompanies = response.data.data.allCompanies;
        const result = allCompanies.map((company) => ({
          value: company._id,
          label: company.name
        }));

        if (result) {
          setCompanyOptions(result);
          if (selectedPartnerPricing) {
            const selectedCompany = result.find((company) => company.value === selectedPartnerPricing.company._id);
            setPartnerCompany(selectedCompany);
          }
          if (defaultCompany) {
            const selectedCompany = result.find((company) => company.value === defaultCompany);
            setPartnerCompany(selectedCompany);
          }
        }
      }
    } catch (error) {
      console.error("Error loading partner companies:", error);
    }
  };

  const getAllProducts = async () => {
    try {
      const response = await api("v1/product", "GET", null);
      if (response.data.success) {
        const allProducts = response.data.data.allProducts;
        const result = allProducts.map((product) => ({
          value: product._id,
          label: product.name,
          price: product.price // Include price information
        }));

        if (result) {
          setProductOptions(result);
          if (selectedPartnerPricing) {
            const selectedProduct = result.find((product) => product.value === selectedPartnerPricing.product._id);
            setProduct(selectedProduct);
          }
        }
      }
    } catch (error) {
      console.error("Error loading partner companies:", error);
    }
  };

  useEffect(() => {
    getAllPartnerCompanies();
    getAllProducts();
  }, []);

  // Update price when editing and product is loaded
  useEffect(() => {
    if (selectedPartnerPricing && product && product.price) {
      console.log("Editing mode: Product loaded, price should be:", product.price);
      // The price will be set in the Formik initialValues, but we can log it here
    }
  }, [selectedPartnerPricing, product]);

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("Submitted Data", values);
    const dataToSend = {
      partnerCompany: partnerCompany.value,
      product: product.value,
      price: values.price
    };
    console.log("Data to Send", dataToSend);
    if (Object.keys(partnerCompany).length == 0 || Object.keys(product).length == 0) {
      // Having issue with YUP validation will use this method for now.
      showErrorToast("Required Fields not selected");
      return false;
    }
    try {
      let response;
      if (selectedPartnerPricing) {
        // Edit PartnerPricing Option
        response = await api(`v1/pricing/${selectedPartnerPricing._id}`, "PATCH", dataToSend);
      } else {
        // Add PartnerPricing Option
        response = await api(`v1/pricing`, "POST", dataToSend);
      }

      if (response.data.success) {
        if (selectedPartnerPricing) {
          console.log("PartnerPricing Updated: ", response.data.data.updatedPartnerPricing);
          showSuccessToast("Partner pricing updated successfully!");
          const partnerCompany = await api(
            `v1/partner/company/${response.data.data.updatedPartnerPricing.partnerCompany}`,
            "GET"
          );
          const product = await api(`v1/product/${response.data.data.updatedPartnerPricing.product}`, "GET");
          // console.log(response.data.data.partnerPricing);
          response.data.data.updatedPartnerPricing.company = partnerCompany.data.data.partnerCompany;
          response.data.data.updatedPartnerPricing.product = product.data.data.product;
          callback(response.data.data.updatedPartnerPricing);
        } else {
          // For new partner pricing creation
          if (response.data.data.newPartnerPricing) {
            console.log("New PartnerPricing Created: ", response.data.data.newPartnerPricing);
            // Show success message
            showSuccessToast("Partner pricing created successfully!");
            // Reset form state
            setPartnerCompany("");
            setProduct("");
            // Call the callback to refresh the list
            callback(response.data.data.newPartnerPricing);
            // Close the modal
            cancelModal();
          } else {
            showErrorToast(response.data.message || "Failed to create partner pricing");
          }
        }
      } else {
        // Handle specific error cases
        const errorMessage = response?.data?.message || "An error occurred";
        if (errorMessage.toLowerCase().includes("already exists") || errorMessage.toLowerCase().includes("duplicate")) {
          showErrorToast(
            "This partner pricing combination already exists. Please check the list or try different values."
          );
        } else {
          showErrorToast(errorMessage);
        }
      }
    } catch (error) {
      console.error("API error:", error);
      showErrorToast("An error occurred while processing the request. Please try again.");
    }

    setSubmitting(false);
  };

  const loadProductOptions = async (inputValue) => {
    try {
      const filteredOptions = productOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      // callback(filteredOptions);
      return filteredOptions;
    } catch (error) {
      console.error("Error loading product options:", error);
      callback([]);
    }
  };

  const handleProductChange = (selectedOptions, setFieldValue) => {
    if (selectedOptions) {
      setProduct(selectedOptions);
      // Auto-set the product price when a product is selected
      const selectedProduct = productOptions.find((p) => p.value === selectedOptions.value);
      console.log("Product changed:", selectedProduct?.label, "Price:", selectedProduct?.price);

      if (selectedProduct && selectedProduct.price) {
        console.log("Setting price field to:", selectedProduct.price);
        setFieldValue("price", selectedProduct.price);
      } else {
        // Reset price to 0 if product has no price
        console.log("Product has no price, setting to 0");
        setFieldValue("price", 0);
      }
    } else {
      setProduct(null);
      console.log("Product cleared, resetting price to 0");
      // Reset price to 0 when product is cleared
      setFieldValue("price", 0);
    }
  };

  const loadCompanyOptions = async (inputValue) => {
    try {
      const filteredOptions = companyOptions.filter((option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      // callback(filteredOptions);
      return filteredOptions;
    } catch (error) {
      console.error("Error loading company options:", error);
      callback([]);
    }
  };

  const handleCompanyChange = (selectedOptions) => {
    if (selectedOptions) {
      setPartnerCompany(selectedOptions);
    } else {
      setPartnerCompany(null);
    }
  };

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "white",
      borderColor: "#9e9e9e",
      zIndex: 2
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 3
    })
  };

  console.log({
    price: selectedPartnerPricing ? selectedPartnerPricing.price : "",
    partnerCompany: partnerCompany,
    product: product
  });

  return (
    <Formik
      initialValues={{
        price: selectedPartnerPricing ? selectedPartnerPricing.price : 0,
        partnerCompany: selectedPartnerPricing ? selectedPartnerPricing.partnerCompany : "",
        product: selectedPartnerPricing ? selectedPartnerPricing.product : ""
      }}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <Form className="form w-full flex flex-column justify-between priorityoneform min-h-[20vh]">
        <div className="form-input-wrap flex gap-2 w-full">
          <div className="w-full flex gap-5">
            <div className="form-group w-full">
              <label className="label" htmlFor="partnerCompany">
                Partner Company:
              </label>
              {/* <Field name="partnerCompany" className="z-2">
                                {({ field }) => (
                                    <AsyncSelect
                                        name="partnerCompany"
                                        {...field}
                                        cacheOptions
                                        defaultOptions
                                        loadOptions={loadCompanyOptions}
                                        styles={selectStyles}
                                        onChange={handleCompanyChange}
                                        value={partnerCompany}
                                    />
                                )}
                            </Field> */}
              <AsyncSelect
                name="partnerCompany"
                cacheOptions
                defaultOptions={companyOptions}
                loadOptions={loadCompanyOptions}
                styles={selectStyles}
                onChange={handleCompanyChange}
                value={partnerCompany}
              />
              <ErrorMessage name="partnerCompany" component="span" className="error-message" />
            </div>
          </div>
          <div className="w-full flex gap-5">
            <div className="form-group w-full">
              <label className="label" htmlFor="product">
                Product:
              </label>
              {/* <Field name="product" className="z-2">
                                {({ field }) => (
                                    <AsyncSelect
                                        name="product"
                                        {...field}
                                        cacheOptions
                                        defaultOptions
                                        loadOptions={loadProductOptions}
                                        styles={selectStyles}
                                        onChange={handleProductChange}
                                        value={product}
                                    />
                                )}
                            </Field> */}
              <Field name="product">
                {({ field, form: { setFieldValue } }) => (
                  <AsyncSelect
                    name="product"
                    {...field}
                    cacheOptions
                    defaultOptions={productOptions}
                    loadOptions={loadProductOptions}
                    styles={selectStyles}
                    onChange={(selectedOptions) => handleProductChange(selectedOptions, setFieldValue)}
                    value={product}
                  />
                )}
              </Field>
              <ErrorMessage name="product" component="span" className="error-message" />
            </div>
          </div>
          <div className="w-full flex gap-5">
            <div className="form-group w-full">
              {/* <label htmlFor="price">Price:</label> */}
              <Field name="price">
                {({ field }) => (
                  <TextField
                    {...field}
                    id="price"
                    type="number"
                    label="Price (Including GST)"
                    className="z-2"
                    inputProps={{
                      min: 0,
                      step: 0.01,
                      precision: 2
                    }}
                    placeholder="Enter price (e.g., 99.99)"
                    helperText="Enter price with up to 2 decimal places"
                  />
                )}
              </Field>
              <ErrorMessage name="price" component="span" className="error-message" />
            </div>
          </div>
        </div>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Button variant="contained" onClick={cancelModal}>
            Cancel
          </Button>
          <Button type="submit" variant="contained">
            Submit
          </Button>
        </Box>
      </Form>
    </Formik>
  );
};

export default PartnerPricingForm;
